import React from "react";
import {
  IonModal,
  IonContent,
  IonButton,
  IonItem,
  IonLabel,
  IonInput,
  IonIcon,
} from "@ionic/react";
import { close } from "ionicons/icons";
import { GlassCard } from "./ui/glass-card";

interface ModalInput {
  name: string;
  type: "text" | "email" | "password" | "number";
  placeholder: string;
  value?: string;
}

interface ModalButton {
  text: string;
  role?: "cancel" | "destructive";
  handler?: (data?: any) => void;
}

interface CustomModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  header?: string;
  message?: string;
  inputs?: ModalInput[];
  buttons?: ModalButton[];
}

const CustomModal: React.FC<CustomModalProps> = ({
  isOpen,
  onDidDismiss,
  header,
  message,
  inputs = [],
  buttons = [],
}) => {
  const [inputValues, setInputValues] = React.useState<Record<string, string>>(
    {},
  );

  React.useEffect(() => {
    if (isOpen) {
      const initialValues: Record<string, string> = {};
      inputs.forEach((input) => {
        initialValues[input.name] = input.value || "";
      });
      setInputValues(initialValues);
    }
  }, [isOpen, inputs]);

  const handleInputChange = (name: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleButtonClick = (button: ModalButton) => {
    if (button.role === "cancel") {
      button.handler?.();
      onDidDismiss();
      return;
    }
    button.handler?.(inputs.length ? inputValues : undefined);
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="backdrop-blur-md"
      style={{ "--height": "auto", "--width": "auto" }}
    >
      <div className="flex justify-center items-center min-h-full">
        <div className="relative w-full max-w-md rounded-2xl p-6 space-y-6 text-white backdrop-blur-xl border border-white/10 shadow-2xl bg-[#1d1d1f]/90">
          {/* HEADER */}
          <div className="text-center">
            <h2 className="text-xl font-bold gradient-text">{header}</h2>
            <IonIcon
              onClick={onDidDismiss}
              icon={close}
              className="text-white size-6 absolute top-[16px] right-[16px] cursor-pointer"
              style={{
                color: "white",
                fontSize: "32px",
              }}
            />
          </div>

          {message && (
            <p className="text-center text-white/80 text-sm mt-2">{message}</p>
          )}

          {/* INPUTS */}
          {inputs.length > 0 && (
            <div className="space-y-4">
              {inputs.map((input, index) => (
                <div key={index} className="space-y-1">
                  <IonLabel className="text-sm text-white/70 block">
                    {input.placeholder}
                  </IonLabel>
                  <IonInput
                    type={input.type}
                    value={inputValues[input.name] || ""}
                    onIonChange={(e) =>
                      handleInputChange(input.name, e.detail.value!)
                    }
                    placeholder={input.placeholder}
                    className="w-full rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/40 p-3 backdrop-blur-md"
                  />
                </div>
              ))}
            </div>
          )}

          {/* BUTTONS */}
          {buttons.length > 0 && (
            <div className="flex flex-col justify-center sm:flex-row gap-3 pt-2">
              {buttons.map((button, index) => (
                <IonButton
                  key={index}
                  onClick={() => handleButtonClick(button)}
                  className={`w-full sm:w-auto h-11 font-semibold rounded-xl transition
                      ${
                        button.role === "cancel"
                          ? "border border-white/20 text-white bg-transparent hover:bg-white/10"
                          : button.role === "destructive"
                          ? "bg-gradient-to-r from-red-500 to-red-700 text-white hover:opacity-90"
                          : "bg-gradient-to-r from-teal-400 to-teal-600 text-white hover:opacity-90"
                      }`}
                  fill="clear"
                >
                  {button.text}
                </IonButton>
              ))}
            </div>
          )}
        </div>
      </div>
    </IonModal>
  );
};

export default CustomModal;
